import React, { useEffect, useRef } from "react";
import { useSortedCompetitors } from "@/features/race/hooks/useSortedCompetitors";
import { useRacingEvent } from "@/hooks/firestore/betting/useRacingEvents";
import { useToggleSelection } from "@/features/betslip/hooks/useToggleSelection";
import { useDockActions } from "@/features/shell/hooks/useDock";
import type {
  ProcessedMarket,
  ProcessedOutcome,
} from "@/features/betslip/builders/types";
import type { Nullable } from "types/utilities";
import type { PickslipPick } from "@/features/pickems/types/PickslipPick";

type AddRacePicksToBetslipProps = {
  picks: PickslipPick[];
  eventId: string;
  entryId: string;
};

const AddRacePicksToBetslip = ({
  picks,
  eventId,
  entryId,
}: AddRacePicksToBetslipProps) => {
  const { toggle } = useToggleSelection();
  const { openDock } = useDockActions();
  const hasAdded = useRef(false);

  const { event, isLoading: raceLoading } = useRacingEvent(eventId);
  const {
    isLoading: competitorsLoading,
    competitors,
    markets,
  } = useSortedCompetitors(eventId);

  const isDataLoading = raceLoading || competitorsLoading;

  useEffect(() => {
    if (hasAdded.current) return;
    if (isDataLoading || !competitors || !event || !markets) return;

    picks.forEach((pick) => {
      if (!pick.eventId) return;

      // find the betting outcome counterpart; this will be removed when
      // backend adds the `bettingOutcomeId` property
      const resolved = markets.reduce<
        Nullable<{
          market: ProcessedMarket;
          outcome: ProcessedOutcome;
          outcomeId: string;
        }>
      >((acc, market) => {
        if (acc) return acc;
        if (market.id !== pick.bettingMarketId) return null;

        const entry = Object.entries(market.outcomes).find(
          ([, outcome]) =>
            outcome.type === pick.outcome?.type &&
            outcome.competitorId === pick.outcome?.competitorId,
        );
        return entry
          ? { market, outcome: entry[1], outcomeId: entry[0] }
          : null;
      }, null);

      if (!resolved) return;

      toggle({
        kind: "race",
        event,
        market: resolved.market,
        outcome: resolved.outcome,
        outcomeId: resolved.outcomeId,
        hub: "racing",
        markets,
        competitors,
        originType: "PICKEMS",
        originId: entryId,
      });
    });

    openDock("betslip");
    hasAdded.current = true;
  }, [
    eventId,
    event,
    markets,
    competitors,
    isDataLoading,
    picks,
    toggle,
    entryId,
    openDock,
  ]);

  return <></>;
};

export { AddRacePicksToBetslip };
